import React from 'react';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { CheckCircle, AlertTriangle, HelpCircle, Info, X } from 'lucide-react';

const SessionConfirmDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Confirmation",
  description = "Êtes-vous sûr de vouloir effectuer cette action ?",
  confirmText = "Confirmer",
  cancelText = "Annuler",
  type = "info", // info, success, warning, error, question
  customIcon = null
}) => {
  // ✅ MODIFICATION PRINCIPALE : onConfirm ne ferme PAS automatiquement
  const handleConfirm = async () => {
    console.log('🔧 [SessionConfirmDialog] Exécution onConfirm sans fermeture auto');
    await onConfirm();
    // ✅ PAS d'appel à onClose() ici - c'est onConfirm qui gère la fermeture
  };

  const handleCancel = () => {
    console.log('🔧 [SessionConfirmDialog] Bouton Cancel cliqué');
    onClose();
  };

  const handleOpenChange = (open) => {
    // Ne ferme le dialog que si on demande explicitement de le fermer (open = false)
    // Mais empêche la fermeture automatique par clic extérieur
    if (!open) {
      console.log('🔧 [SessionConfirmDialog] Tentative fermeture par overlay - ignorée');
      // Ne rien faire - seuls les boutons peuvent fermer le dialog
      return;
    }
  };

  // Fonction pour obtenir l'icône et la couleur selon le type
  const getIconAndColor = () => {
    if (customIcon) {
      return { icon: customIcon, color: "text-blue-600", bgColor: "bg-blue-50" };
    }

    switch (type) {
      case 'success':
        return { 
          icon: <CheckCircle size={32} />, 
          color: "text-green-600",
          bgColor: "bg-green-50"
        };
      case 'warning':
        return { 
          icon: <AlertTriangle size={32} />, 
          color: "text-yellow-600",
          bgColor: "bg-yellow-50"
        };
      case 'error':
        return { 
          icon: <X size={32} />, 
          color: "text-red-600",
          bgColor: "bg-red-50"
        };
      case 'question':
        return { 
          icon: <HelpCircle size={32} />, 
          color: "text-blue-600",
          bgColor: "bg-blue-50"
        };
      default: // info
        return { 
          icon: <Info size={32} />, 
          color: "text-blue-600",
          bgColor: "bg-blue-50"
        };
    }
  };

  const { icon, color, bgColor } = getIconAndColor();

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={handleOpenChange} >
      <DialogPortal>
        {/* ✅ Z-index très élevé pour l'overlay */}
        <DialogOverlay className="z-[9999]" />
        {/* ✅ Z-index très élevé pour le contenu */}
        <DialogContent className="sm:max-w-md z-[10000]">
          <DialogHeader className="text-center">
            {/* Zone d'icône */}
            <div className={`mx-auto w-14 h-14 rounded-full ${bgColor} flex items-center justify-center mb-4`}>
              <div className={color}>
                {icon}
              </div>
            </div>
            <DialogTitle className="text-xl">{title}</DialogTitle>
            <DialogDescription className="mt-2">
              {description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
            <Button 
              variant="outline" 
              onClick={handleCancel}  // ✅ Utilise handleCancel
              className="text-base px-5 py-2 h-auto"
            >
              {cancelText}
            </Button>
            <Button 
              onClick={handleConfirm}  // ✅ Ne ferme pas automatiquement
              className="flex items-center gap-2 bg-[#246990] hover:bg-[#246990]/90 text-white text-base px-5 py-2 h-auto rounded-lg"
            >
              {confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};

export default SessionConfirmDialog;